import { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { AppModal } from '../components/AppModal';
import {
  getEvents,
  createEventDef, modifyEventDef, deleteEventDef,
  createEventSlot, modifyEventSlot, deleteEventSlot,
} from '../services/EventScheduleService';
import { FormFeedback } from '../components/FormFeedback';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { StaffPageHeader } from '../components/StaffPageHeader';
import { CalendarEventFill } from 'react-bootstrap-icons';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Display Mon–Sun without touching bit indices (bit 0 = Sun, bit 1 = Mon, …, bit 6 = Sat)
const DAY_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
function isValidTime(t) { return TIME_RE.test(t); }

function dayBit(i) { return 1 << i; }
function isDaySet(mask, i) { return !!(mask & dayBit(i)); }
function toggleDay(mask, i, on) { return on ? mask | dayBit(i) : mask & ~dayBit(i); }

// ─── Definition Form ──────────────────────────────────────────────────────────

const EMPTY_DEF_FORM = { title: '', description: '', color: '#38bdf8', eventType: '' };

function formFromDef(def) {
  if (!def) return EMPTY_DEF_FORM;
  return {
    title:       def.Title       || '',
    description: def.Description || '',
    color:       def.Color       || '#38bdf8',
    eventType:   def.EventType   || '',
  };
}

function DefinitionForm({ editing, onSaved, onClose }) {
  const [form, setForm] = useState(() => formFromDef(editing));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setError(null);

    const payload = {
      title:       form.title.trim(),
      description: form.description.trim() || null,
      color:       form.color,
      eventType:   form.eventType.trim() || null,
    };

    setSubmitting(true);
    try {
      if (editing) {
        await modifyEventDef({ defId: editing.ID, ...payload });
        onSaved({
          ...editing,
          Title: payload.title, Description: payload.description,
          Color: payload.color, EventType: payload.eventType,
        });
      } else {
        const newId = await createEventDef(payload);
        onSaved({
          ID: newId, Title: payload.title, Description: payload.description,
          Color: payload.color, EventType: payload.eventType, Schedules: [],
        });
      }
    } catch (err) {
      setError(err.message || 'An error occurred.');
      setSubmitting(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} className="modal-form">
      <Form.Group className="mb-3">
        <Form.Label>Title *</Form.Label>
        <Form.Control
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="e.g. Kingdom War"
          disabled={submitting}
          maxLength={255}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder="Optional details shown in tooltip"
          disabled={submitting}
        />
      </Form.Group>

      <Row className="mb-3">
        <Col xs={6}>
          <Form.Label>Color</Form.Label>
          <div className="sev-color-row">
            <Form.Control
              type="color"
              value={form.color}
              onChange={e => set('color', e.target.value)}
              disabled={submitting}
              className="sev-color-input"
            />
            <span className="sev-color-hex">{form.color}</span>
          </div>
        </Col>
        <Col xs={6}>
          <Form.Label>Event Type</Form.Label>
          <Form.Control
            value={form.eventType}
            onChange={e => set('eventType', e.target.value)}
            placeholder="e.g. Boss, PK, War"
            disabled={submitting}
            maxLength={50}
          />
        </Col>
      </Row>

      <FormFeedback type="error" message={error} />
      <div className="modal-form-actions">
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="success" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Event'}
        </Button>
      </div>
    </Form>
  );
}

// ─── Slot Form ────────────────────────────────────────────────────────────────

const EMPTY_SLOT_FORM = {
  isRecurring: true,
  recurDays:   0,         // Monday by default (bit 1)
  recurStart:  '20:00',
  recurEnd:    '',
  eventStart:  '',
  eventEnd:    '',
};

function formFromSlot(slot) {
  if (!slot) return EMPTY_SLOT_FORM;
  return {
    isRecurring: !!slot.IsRecurring,
    recurDays:   slot.RecurDays  ?? 2,
    recurStart:  slot.RecurStart ? slot.RecurStart.slice(0, 5) : '20:00',
    recurEnd:    slot.RecurEnd   ? slot.RecurEnd.slice(0, 5)   : '',
    eventStart:  slot.EventStart ? slot.EventStart.replace(' ', 'T').slice(0, 16) : '',
    eventEnd:    slot.EventEnd   ? slot.EventEnd.replace(' ', 'T').slice(0, 16)   : '',
  };
}

function toSlotShape(p) {
  return {
    IsRecurring: p.isRecurring ? 1 : 0,
    RecurDays:   p.recurDays,
    RecurStart:  p.recurStart,
    RecurEnd:    p.recurEnd,
    EventStart:  p.eventStart,
    EventEnd:    p.eventEnd,
  };
}

function SlotForm({ editing, defId, onSaved, onClose }) {
  const [form, setForm] = useState(() => formFromSlot(editing));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function toggleRecurDay(i, checked) {
    setForm(prev => ({ ...prev, recurDays: toggleDay(prev.recurDays, i, checked) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.isRecurring && !form.recurDays) { setError('Select at least one day.'); return; }
    if (form.isRecurring && !isValidTime(form.recurStart)) { setError('Start time must be HH:MM (e.g. 20:00).'); return; }
    if (form.isRecurring && form.recurEnd && !isValidTime(form.recurEnd)) { setError('End time must be HH:MM (e.g. 21:00).'); return; }
    if (!form.isRecurring && !form.eventStart) { setError('Event start date/time is required.'); return; }
    setError(null);

    const payload = {
      eventId:     defId,
      isRecurring: form.isRecurring,
      recurDays:   form.isRecurring ? form.recurDays : null,
      recurStart:  form.isRecurring ? form.recurStart + ':00' : null,
      recurEnd:    form.isRecurring && form.recurEnd ? form.recurEnd + ':00' : null,
      eventStart:  !form.isRecurring ? form.eventStart.replace('T', ' ') : null,
      eventEnd:    !form.isRecurring && form.eventEnd ? form.eventEnd.replace('T', ' ') : null,
    };

    setSubmitting(true);
    try {
      if (editing) {
        await modifyEventSlot({ slotId: editing.ID, ...payload });
        onSaved({ ...editing, ...toSlotShape(payload) });
      } else {
        const newId = await createEventSlot(payload);
        onSaved({ ID: newId, EventID: defId, ...toSlotShape(payload) });
      }
    } catch (err) {
      setError(err.message || 'An error occurred.');
      setSubmitting(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} className="modal-form">
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="recurring-switch"
          label="Recurring weekly event"
          checked={form.isRecurring}
          onChange={e => set('isRecurring', e.target.checked)}
          disabled={submitting}
        />
      </Form.Group>

      {form.isRecurring ? (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Days *</Form.Label>
            <div className="sev-day-picker">
              {DAY_DISPLAY_ORDER.map(i => (
                <button
                  key={i}
                  type="button"
                  className={`sev-day-btn${isDaySet(form.recurDays, i) ? ' sev-day-btn--on' : ''}`}
                  onClick={() => toggleRecurDay(i, !isDaySet(form.recurDays, i))}
                  disabled={submitting}
                  aria-pressed={isDaySet(form.recurDays, i)}
                >
                  {DAY_SHORT[i]}
                </button>
              ))}
            </div>
          </Form.Group>

          <Row className="mb-3 gx-2">
            <Col xs={6}>
              <Form.Label>Start Time * <span className="sev-time-hint">HH:MM</span></Form.Label>
              <Form.Control
                type="text"
                value={form.recurStart}
                onChange={e => set('recurStart', e.target.value)}
                placeholder="e.g. 20:00"
                maxLength={5}
                disabled={submitting}
                isInvalid={form.recurStart.length > 0 && !isValidTime(form.recurStart)}
              />
            </Col>
            <Col xs={6}>
              <Form.Label>End Time <span className="sev-time-hint">HH:MM</span></Form.Label>
              <Form.Control
                type="text"
                value={form.recurEnd}
                onChange={e => set('recurEnd', e.target.value)}
                placeholder="e.g. 21:00"
                maxLength={5}
                disabled={submitting}
                isInvalid={form.recurEnd.length > 0 && !isValidTime(form.recurEnd)}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row className="mb-3">
          <Col xs={12} sm={6}>
            <Form.Label>Start *</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.eventStart}
              onChange={e => set('eventStart', e.target.value)}
              disabled={submitting}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Form.Label>End</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.eventEnd}
              onChange={e => set('eventEnd', e.target.value)}
              disabled={submitting}
            />
          </Col>
        </Row>
      )}

      <FormFeedback type="error" message={error} />
      <div className="modal-form-actions">
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="success" type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Add Time Slot'}
        </Button>
      </div>
    </Form>
  );
}

// ─── Slot schedule display ────────────────────────────────────────────────────

function formatSlotSchedule(slot) {
  if (slot.IsRecurring) {
    const mask = slot.RecurDays || 0;
    const days = DAY_DISPLAY_ORDER.filter(i => isDaySet(mask, i)).map(i => DAY_NAMES[i]);
    const daysStr = days.length > 0 ? days.join(', ') : '?';
    const start = slot.RecurStart ? slot.RecurStart.slice(0, 5) : '?';
    const end = slot.RecurEnd ? ` \u2013 ${slot.RecurEnd.slice(0, 5)}` : '';
    return `Every ${daysStr} at ${start}${end}`;
  }
  if (slot.EventStart) {
    const start = new Date(slot.EventStart).toLocaleString();
    const end = slot.EventEnd ? ` \u2013 ${new Date(slot.EventEnd).toLocaleString()}` : '';
    return `${start}${end}`;
  }
  return '\u2014';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StaffEventSchedule() {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [defModal, setDefModal] = useState({ show: false, editing: null });
  const [slotModal, setSlotModal] = useState({ show: false, editing: null, defId: null });

  const [confirmDeleteDefId, setConfirmDeleteDefId] = useState(null);
  const [confirmDeleteSlotId, setConfirmDeleteSlotId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getEvents();
        if (!cancelled) setDefs(data || []);
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function toggleExpand(defId) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(defId) ? next.delete(defId) : next.add(defId);
      return next;
    });
  }

  // ── Definition CRUD ──

  function openCreateDef() {
    setDefModal({ show: true, editing: null });
  }

  function openEditDef(def) {
    setDefModal({ show: true, editing: def });
  }

  function closeDefModal() {
    setDefModal({ show: false, editing: null });
  }

  function handleDefSaved(savedDef) {
    setDefs(prev => {
      const idx = prev.findIndex(d => d.ID === savedDef.ID);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...savedDef };
        return next;
      }
      return [...prev, savedDef];
    });
    closeDefModal();
  }

  async function handleDeleteDef(def) {
    if (confirmDeleteDefId === def.ID) {
      await deleteEventDef(def.ID);
      setDefs(prev => prev.filter(d => d.ID !== def.ID));
      setConfirmDeleteDefId(null);
    } else {
      setConfirmDeleteDefId(def.ID);
      setConfirmDeleteSlotId(null);
    }
  }

  // ── Slot CRUD ──

  function openCreateSlot(defId) {
    setSlotModal({ show: true, editing: null, defId });
    setExpandedIds(prev => new Set(prev).add(defId));
  }

  function openEditSlot(defId, slot) {
    setSlotModal({ show: true, editing: slot, defId });
  }

  function closeSlotModal() {
    setSlotModal({ show: false, editing: null, defId: null });
  }

  function handleSlotSaved(defId, savedSlot) {
    setDefs(prev => prev.map(d => {
      if (d.ID !== defId) return d;
      const slots = d.Schedules || [];
      const idx = slots.findIndex(s => s.ID === savedSlot.ID);
      if (idx >= 0) {
        const next = [...slots];
        next[idx] = savedSlot;
        return { ...d, Schedules: next };
      }
      return { ...d, Schedules: [...slots, savedSlot] };
    }));
    closeSlotModal();
  }

  async function handleDeleteSlot(defId, slot) {
    if (confirmDeleteSlotId === slot.ID) {
      await deleteEventSlot(slot.ID);
      setDefs(prev => prev.map(d =>
        d.ID !== defId ? d : { ...d, Schedules: d.Schedules.filter(s => s.ID !== slot.ID) }
      ));
      setConfirmDeleteSlotId(null);
    } else {
      setConfirmDeleteSlotId(slot.ID);
      setConfirmDeleteDefId(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="sev-page">
      <StaffPageHeader
        icon={<CalendarEventFill />}
        title="Event Schedule"
        actions={<Button variant="success" onClick={openCreateDef}>+ Create New Event</Button>}
      />
      <div className="sev-table-wrap">
        <div className="sev-header">
          <Row>
            <Col xs={1}></Col>
            <Col>Event</Col>
            <Col xs={2} className="sev-type">Type</Col>
            <Col xs={3}></Col>
          </Row>
        </div>

        <div className="sev-body">
          {defs.length === 0 ? (
            <EmptyState message="No events yet." />
          ) : (
            defs.map(def => {
              const expanded = expandedIds.has(def.ID);
              const slots = def.Schedules || [];
              return (
                <div key={def.ID} className="sev-def-block">
                  <Row className="sev-row sev-def-row" onClick={() => toggleExpand(def.ID)}>
                    <Col xs={1}>
                      <span className="sev-color-dot" style={{ backgroundColor: def.Color || '#38bdf8' }} />
                    </Col>
                    <Col className="sev-title">
                      {def.Title}
                      <span className="sev-slot-count">{slots.length} slot{slots.length !== 1 ? 's' : ''}</span>
                    </Col>
                    <Col xs={2} className="sev-type">{def.EventType || '\u2014'}</Col>
                    <Col xs={3} className="sev-actions" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="warning" onClick={() => openEditDef(def)}>Edit</Button>
                      <Button size="sm" variant="danger" onClick={() => handleDeleteDef(def)}>
                        {confirmDeleteDefId === def.ID ? 'Confirm' : 'Delete'}
                      </Button>
                      <button
                        className="sev-expand-btn"
                        onClick={() => toggleExpand(def.ID)}
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                      >
                        {expanded ? '\u25be' : '\u25b8'}
                      </button>
                    </Col>
                  </Row>

                  {expanded && (
                    <div className="sev-slots">
                      {slots.map(slot => (
                        <Row key={slot.ID} className="sev-row sev-slot-row">
                          <Col xs={1} />
                          <Col className="sev-schedule">{formatSlotSchedule(slot)}</Col>
                          <Col xs={3} className="sev-actions">
                            <Button size="sm" variant="secondary" onClick={() => openEditSlot(def.ID, slot)}>Edit</Button>
                            <Button size="sm" variant="danger" onClick={() => handleDeleteSlot(def.ID, slot)}>
                              {confirmDeleteSlotId === slot.ID ? 'Confirm' : 'Delete'}
                            </Button>
                          </Col>
                        </Row>
                      ))}
                      <Row className="sev-row sev-add-slot-row">
                        <Col xs={1} />
                        <Col>
                          <Button size="sm" variant="outline-success" onClick={() => openCreateSlot(def.ID)}>
                            + Add Time Slot
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="sev-footer" />
      </div>

      <AppModal show={defModal.show} onHide={closeDefModal}>
        <AppModal.Header closeButton>
          <AppModal.Title>{defModal.editing ? 'Edit Event' : 'Create Event'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          {defModal.show && (
            <DefinitionForm
              editing={defModal.editing}
              onSaved={handleDefSaved}
              onClose={closeDefModal}
            />
          )}
        </AppModal.Body>
      </AppModal>

      <AppModal show={slotModal.show} onHide={closeSlotModal}>
        <AppModal.Header closeButton>
          <AppModal.Title>{slotModal.editing ? 'Edit Time Slot' : 'Add Time Slot'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          {slotModal.show && (
            <SlotForm
              editing={slotModal.editing}
              defId={slotModal.defId}
              onSaved={savedSlot => handleSlotSaved(slotModal.defId, savedSlot)}
              onClose={closeSlotModal}
            />
          )}
        </AppModal.Body>
      </AppModal>
    </div>
  );
}
